import { CommonModule, DatePipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import { disputeCreditNoteLinks, decisionStepLabel, formatStepDuration, formatTimelineActor } from '../dispute.utils';
import { OrderNavigationService } from '../../services/order-navigation.service';
import {
    StaffDisputeCreditNoteLink,
    StaffDisputeDetail,
    StaffDisputeStatusEvent
} from '../../services/staff-dispute.service';

interface TimelineStep {
    key: string;
    label: string;
    state: 'done' | 'active' | 'pending';
    date?: string;
    by?: string;
    elapsedFromPrevious?: string;
    creditNotes?: StaffDisputeCreditNoteLink[];
}

@Component({
    selector: 'app-dispute-timeline',
    standalone: true,
    imports: [CommonModule, DatePipe],
    template: `
        <div class="timeline-card">
            <h3 class="timeline-title">Status Timeline</h3>
            <ol class="timeline">
                <li *ngFor="let step of steps" [class]="step.state">
                    <span class="dot">
                        <i *ngIf="step.state === 'done'" class="pi pi-check"></i>
                    </span>
                    <div class="step-body">
                        <strong>{{ step.label }}</strong>
                        <span class="step-meta pending" *ngIf="step.state === 'pending'">Pending</span>
                        <span class="step-meta" *ngIf="step.date">{{ step.date | date: 'MMM d, y, h:mm a' }}</span>
                        <div class="elapsed-row" *ngIf="step.elapsedFromPrevious">
                            <span class="elapsed-badge">
                                <i class="pi pi-clock" aria-hidden="true"></i>
                                {{ step.elapsedFromPrevious }}
                            </span>
                            <span class="elapsed-hint">since previous step</span>
                        </div>
                        <span class="step-meta actor" *ngIf="step.by">By {{ step.by }}</span>
                        <ng-container *ngIf="step.creditNotes?.length">
                            <ng-container *ngFor="let cn of step.creditNotes; let i = index">
                                <a
                                    class="step-meta cn-order-link"
                                    href="#"
                                    (click)="onCreditNoteClick($event, cn.creditNoteOrderNumber)"
                                >
                                    {{ cn.creditNoteOrderNumber }}
                                </a>
                                <span class="step-meta" *ngIf="creditNoteDetailsLine(cn)">
                                    {{ creditNoteDetailsLine(cn) }}
                                </span>
                                <span
                                    class="step-meta actor"
                                    *ngIf="creditNoteCreator(cn.createdBy) as creator"
                                >
                                    By {{ creator }}
                                </span>
                                <span
                                    class="step-meta cn-separator"
                                    *ngIf="i < step.creditNotes!.length - 1"
                                ></span>
                            </ng-container>
                        </ng-container>
                    </div>
                </li>
            </ol>
        </div>
    `,
    styles: [
        `
            .timeline-card {
                background: var(--dispute-card, var(--surface-card));
                border: 1px solid var(--dispute-border, var(--surface-border));
                border-radius: 12px;
                padding: 1rem 1.25rem;
                height: fit-content;
                position: sticky;
                top: 1rem;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
            }
            .timeline-title {
                margin: 0 0 1rem;
                font-size: 0.95rem;
                font-weight: 600;
            }
            .timeline {
                list-style: none;
                margin: 0;
                padding: 0;
            }
            .timeline li {
                display: grid;
                grid-template-columns: 24px 1fr;
                gap: 0.75rem;
                padding-bottom: 1.35rem;
                position: relative;
            }
            .timeline li:not(:last-child)::before {
                content: '';
                position: absolute;
                left: 11px;
                top: 24px;
                bottom: 0;
                width: 2px;
                background: var(--dispute-border, var(--surface-border));
            }
            .timeline li.done:not(:last-child)::before {
                background: var(--p-green-500);
            }
            .dot {
                width: 22px;
                height: 22px;
                border-radius: 50%;
                border: 2px solid var(--dispute-border, var(--surface-border));
                background: var(--dispute-card, var(--surface-card));
                margin-top: 2px;
                z-index: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 0.55rem;
                color: var(--dispute-card, var(--surface-card));
            }
            li.done .dot {
                border-color: var(--p-green-500);
                background: var(--p-green-500);
                color: var(--p-green-950);
            }
            li.active .dot {
                border-color: var(--p-orange-500);
                background: var(--dispute-card, var(--surface-card));
                box-shadow: 0 0 0 3px color-mix(in srgb, var(--p-orange-500) 25%, transparent);
            }
            li.pending .dot {
                background: var(--dispute-card-muted, var(--surface-secondary));
            }
            .step-body {
                display: flex;
                flex-direction: column;
                gap: 0.12rem;
                min-width: 0;
            }
            .step-body strong {
                font-size: 0.88rem;
            }
            .step-meta {
                font-size: 0.78rem;
                color: var(--dispute-muted, var(--text-color-secondary));
            }
            .step-meta.actor {
                word-break: break-word;
            }
            .elapsed-row {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 0.35rem 0.5rem;
                margin-top: 0.15rem;
            }
            .elapsed-badge {
                display: inline-flex;
                align-items: center;
                gap: 0.3rem;
                padding: 0.2rem 0.55rem;
                border-radius: 999px;
                font-size: 0.72rem;
                font-weight: 600;
                letter-spacing: 0.01em;
                color: var(--dispute-primary, var(--primary-color));
                background: color-mix(
                    in srgb,
                    var(--dispute-primary, var(--primary-color)) 14%,
                    transparent
                );
                border: 1px solid
                    color-mix(in srgb, var(--dispute-primary, var(--primary-color)) 28%, transparent);
            }
            .elapsed-badge .pi {
                font-size: 0.65rem;
            }
            .elapsed-hint {
                font-size: 0.72rem;
                color: var(--dispute-muted, var(--text-color-secondary));
            }
            .step-meta.pending {
                color: var(--dispute-muted, var(--text-color-secondary));
                font-style: italic;
                opacity: 0.85;
            }
            .cn-order-link {
                display: block;
                padding: 0;
                border: none;
                background: none;
                color: var(--dispute-primary, var(--primary-color));
                font-weight: 600;
                text-decoration: underline;
                text-underline-offset: 2px;
                cursor: pointer;
            }
            .cn-order-link:hover {
                opacity: 0.9;
            }
            .cn-separator {
                display: block;
                height: 0.5rem;
            }
        `
    ]
})
export class DisputeTimelineComponent {
    @Input({ required: true }) detail: StaffDisputeDetail | null = null;

    constructor(private readonly orderNavigation: OrderNavigationService) {}

    creditNoteCreator(raw?: string | null): string | null {
        return formatTimelineActor(raw);
    }

    creditNoteDetailsLine(cn: StaffDisputeCreditNoteLink): string {
        const parts: string[] = [];
        if (cn.creditNoteOrderType) {
            parts.push(cn.creditNoteOrderType);
        }
        if (cn.createdAt) {
            parts.push(
                new Date(cn.createdAt).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                })
            );
        }
        return parts.join(' · ');
    }

    onCreditNoteClick(event: Event, orderNumber: string): void {
        event.preventDefault();
        event.stopPropagation();
        this.viewCreditNoteOrder(orderNumber);
    }

    viewCreditNoteOrder(orderNumber: string): void {
        const trimmed = (orderNumber || '').trim();
        if (!trimmed) {
            return;
        }
        this.orderNavigation.navigateToItemDetails(trimmed, { fromDisputeReview: true });
    }

    get steps(): TimelineStep[] {
        if (!this.detail) return [];
        const status = (this.detail.status || '').toUpperCase();
        const timeline = this.detail.timeline || [];

        const submittedEvent = this.findEvent(timeline, 'SUBMITTED');
        const reviewEvent = this.findEvent(timeline, 'UNDER_REVIEW');
        const approvedEvent = this.findEvent(timeline, 'APPROVED');
        const rejectedEvent = this.findEvent(timeline, 'REJECTED');

        const underReviewDone =
            status === 'UNDER_REVIEW' ||
            status === 'APPROVED' ||
            status === 'PARTIALLY_APPROVED' ||
            status === 'REJECTED';
        const rejected = status === 'REJECTED';
        const approvedDone = status === 'APPROVED' || status === 'PARTIALLY_APPROVED';
        const decisionDone = approvedDone || rejected;
        const creditNotes = disputeCreditNoteLinks(this.detail);

        const submittedDate = submittedEvent?.changedAt || this.detail.createdAt;

        const rawSteps: TimelineStep[] = [
            {
                key: 'submitted',
                label: 'Submitted',
                state: 'done',
                date: submittedDate,
                by: formatTimelineActor(submittedEvent?.changedBy) ?? undefined
            },
            {
                key: 'review',
                label: 'Under Review',
                state: status === 'UNDER_REVIEW' ? 'active' : underReviewDone ? 'done' : 'pending',
                date: reviewEvent?.changedAt,
                by: formatTimelineActor(reviewEvent?.changedBy) ?? undefined
            },
            {
                key: 'decision',
                label: decisionStepLabel(status, approvedDone, rejected),
                state: decisionDone ? 'done' : 'pending',
                date: rejected ? rejectedEvent?.changedAt : approvedEvent?.changedAt,
                by: formatTimelineActor(
                    rejected ? rejectedEvent?.changedBy : approvedEvent?.changedBy
                ) ?? undefined
            },
            {
                key: 'credit',
                label:
                    creditNotes.length > 1
                        ? `Credit notes (${creditNotes.length})`
                        : creditNotes.length === 1
                          ? 'Credit note generated'
                          : 'Credit Note Generated',
                state: creditNotes.length > 0 ? 'done' : 'pending',
                date: creditNotes.length > 0 ? creditNotes[0].createdAt : undefined,
                creditNotes: creditNotes.length > 0 ? creditNotes : undefined
            }
        ];

        return this.attachElapsedDurations(rawSteps);
    }

    private attachElapsedDurations(steps: TimelineStep[]): TimelineStep[] {
        let prevDate: string | undefined;
        return steps.map((step) => {
            const canMeasure =
                prevDate &&
                step.date &&
                (step.state === 'done' || step.state === 'active');
            const elapsed = canMeasure ? formatStepDuration(prevDate, step.date) : undefined;
            const next: TimelineStep = { ...step };
            if (elapsed) {
                next.elapsedFromPrevious = elapsed;
            }
            if (step.date && (step.state === 'done' || step.state === 'active')) {
                prevDate = step.date;
            }
            return next;
        });
    }

    private findEvent(timeline: StaffDisputeStatusEvent[], status: string): StaffDisputeStatusEvent | undefined {
        return timeline.find((e) => (e.newStatus || '').toUpperCase() === status);
    }
}
